import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FileBarChart, Printer, Package, Clock, CheckCircle2, Layers, Loader2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useBranding } from "../hooks/useBranding";
import { listarClientes } from "../services/clientes";
import { getContratadoEntregue, getRelatorioEntregas, getContratadoMensalGeral } from "../services/relatorios";
import { anexoUrlAbsoluto } from "../services/tarefas";

const anoAtual = new Date().getFullYear();

/** Logo recdc (preta+vermelha) em vetor — imprime nítido no PDF, fundo branco. */
function RecLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 330 92" width={252} height={70} className={className} role="img" aria-label="recdc">
      <text x="0" y="72" fontFamily="'Arial Black','Arial Narrow',Arial,sans-serif" fontWeight={900} fontSize="86" letterSpacing="-7" fill="#0b0b0b">rec</text>
      <circle cx="190" cy="46" r="15" fill="#c8102e" />
      <text x="214" y="72" fontFamily="'Arial Black','Arial Narrow',Arial,sans-serif" fontWeight={900} fontSize="86" letterSpacing="-7" fill="#c8102e">dc</text>
      <text x="306" y="24" fontFamily="Arial,sans-serif" fontSize="20" fill="#0b0b0b">®</text>
    </svg>
  );
}

function fmtFormato(f: string | null) {
  if (!f) return "";
  return f.split(",").map((s) => s.trim()).filter(Boolean).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(", ");
}
function fmtHoras(ms: number) {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${String(m).padStart(2, "0")}`;
}
/** MELHORIA 22 — horas VENDIDAS (número decimal): "10h" / "10,5h" / "—". */
function fmtH(h: number) {
  if (!h) return "—";
  return `${String(h).replace(".", ",")}h`;
}
const STATUS_LABEL: Record<string, string> = {
  planejamento: "Planejamento", ativo: "Ativo", em_validacao: "Em validação",
  entregue: "Entregue", concluido: "Concluído", pausado: "Pausado",
};

function mesAtual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
/** Primeiro e último dia do mês "YYYY-MM", em data LOCAL (ver utils/periodo do backend). */
function faixaDoMes(mes: string): { inicio: string; fim: string } {
  const [y, m] = mes.split("-").map(Number);
  const ultimo = new Date(y, m, 0).getDate(); // dia 0 do mês seguinte = último deste
  return { inicio: `${mes}-01`, fim: `${mes}-${String(ultimo).padStart(2, "0")}` };
}

/**
 * CONTRATADO NO MÊS — todos os clientes juntos (pedido da Thainá, ago/2026:
 * "onde vejo a quantidade total de entregas mensais que tenho fechado, pra
 * prever setembro?"). O relatório abaixo é de UM cliente por vez; este painel
 * responde o volume da agência inteira, somando os itens de contrato.
 *
 * Default = só contratos MENSAIS: são os que se repetem e servem pra prever o
 * mês seguinte. Projeto avulso também tem itens de contrato, mas é escopo de uma
 * vez só — por isso fica atrás de um botão, e não somado por padrão.
 */
function ContratadoNoMes() {
  const [mes, setMes] = useState(mesAtual());
  const [apenasMensais, setApenasMensais] = useState(true);
  const { inicio, fim } = faixaDoMes(mes);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["contratado-mensal", inicio, fim, apenasMensais],
    queryFn: () => getContratadoMensalGeral(inicio, fim, apenasMensais),
    placeholderData: keepPreviousData,
  });

  const cats = data?.categorias ?? [];
  const maxCat = Math.max(1, ...cats.map((c) => c.contratado));
  const botao = (ativo: boolean) =>
    `px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${ativo ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"}`;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 print:hidden">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-semibold flex items-center gap-2 text-slate-800">
            <Layers size={18} className="text-emerald-600" /> Contratado no mês — todos os clientes
          </h3>
          <p className="text-slate-500 text-xs mt-1">
            Quantas peças a Rec tem fechada por mês, somando os contratos. Use para prever o mês seguinte.
          </p>
          <p className="text-slate-400 text-[11px] mt-1">
            <span className="text-emerald-600 font-medium">Entregues</span> = cards finalizados no mês ·{" "}
            <span className="text-sky-600 font-medium">Andando</span> = já têm card rodando no quadro (foto de agora) ·{" "}
            <span className="font-medium">Do zero</span> = falta começar
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
            <button type="button" onClick={() => setApenasMensais(true)} className={botao(apenasMensais)} title="Só projetos de contrato mensal (recorrente)">
              Contratos mensais
            </button>
            <button type="button" onClick={() => setApenasMensais(false)} className={botao(!apenasMensais)} title="Inclui também projetos avulsos (escopo de uma vez só)">
              Todos
            </button>
          </div>
          <input
            type="month" value={mes} onChange={(e) => setMes(e.target.value || mesAtual())}
            className="px-2 py-1.5 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500"
            title="Mês de referência"
          />
        </div>
      </div>

      {isError ? (
        <p className="text-red-500 text-sm">Erro ao carregar o contratado.</p>
      ) : isLoading && !data ? (
        <p className="text-slate-400 text-sm">Carregando…</p>
      ) : cats.length === 0 ? (
        <p className="text-slate-400 text-sm">
          Nenhum item de contrato {apenasMensais ? "em projeto mensal" : "cadastrado"}. Os itens ficam no cadastro do projeto.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-6 mb-4 pb-4 border-b border-slate-100">
            <div>
              <p className="text-3xl font-bold text-slate-800 tabular-nums leading-none">{data!.totais.contratado}</p>
              <p className="text-xs text-slate-500 mt-1">peças/mês contratadas</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-emerald-600 tabular-nums leading-none">{data!.totais.entregue}</p>
              <p className="text-xs text-slate-500 mt-1">entregues no mês escolhido</p>
              {/* Entrega é fato: continua contando mesmo que o contrato tenha
                  sido encerrado ou o projeto arquivado depois. */}
              {data!.totais.entregueEncerrados > 0 && (
                <p className="text-[11px] text-slate-400 mt-0.5">
                  inclui {data!.totais.entregueEncerrados} de contratos já encerrados
                </p>
              )}
            </div>
            <div title="Peças com card já rodando no quadro agora (passou de 'Pronto para ser executado')">
              <p className="text-2xl font-semibold text-sky-600 tabular-nums leading-none">{data!.totais.emAndamento}</p>
              <p className="text-xs text-slate-500 mt-1">em andamento agora</p>
            </div>
            <div title="Peças contratadas que ainda não têm card rodando — o que falta começar">
              <p className="text-2xl font-semibold text-slate-400 tabular-nums leading-none">{data!.totais.aFazer}</p>
              <p className="text-xs text-slate-500 mt-1">a fazer do zero</p>
            </div>
            <div className="text-xs text-slate-500">
              {data!.totais.clientes} {data!.totais.clientes === 1 ? "cliente" : "clientes"} ·{" "}
              {data!.totais.projetos} {data!.totais.projetos === 1 ? "projeto" : "projetos"}
              {data!.totais.encerrados > 0 && (
                <span className="block mt-0.5 text-slate-400">
                  {data!.totais.encerrados} fora da conta (contrato encerrado)
                </span>
              )}
            </div>
          </div>

          {/* Enquanto o fim do contrato não é preenchido, o projeto conta em
              TODO mês futuro e a previsão fica maior que a realidade. */}
          {data!.totais.semDataFim > 0 && (
            <div className="mb-4 -mt-1 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <span aria-hidden>⚠️</span>
              <span>
                <strong>{data!.totais.semDataFim}</strong>{" "}
                {data!.totais.semDataFim === 1 ? "projeto está" : "projetos estão"} sem{" "}
                <strong>fim de contrato</strong> cadastrado, então {data!.totais.semDataFim === 1 ? "continua" : "continuam"} contando
                em todos os meses à frente. Para encerrar um cliente: preencha o <em>Fim do contrato</em> no
                projeto, ou desmarque <em>Cliente ativo</em> em Clientes.
              </span>
            </div>
          )}

          {/* Contrato por PONTOS (modelo antigo da Rec: um vídeo = 15 pontos).
              A quantidade dele não é quantidade de peças, então não entra na
              previsão — mas fica visível, senão o cliente sumiria em silêncio. */}
          {/* Explica o balde "Outros" logo abaixo das barras, com o caminho pra
              resolver — senão a pessoa fica sem saber o que fazer com ele. */}
          {data!.tiposEmOutros.length > 0 && data!.categorias.some((c) => c.categoria === "Outros") && (
            <div className="mb-4 -mt-1 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <span className="font-medium text-slate-600">O que é “Outros”:</span>{" "}
              peça cujo tipo ainda não tem categoria definida — hoje {data!.tiposEmOutros.join(", ")}.
              <span className="block mt-0.5 text-slate-400">
                Para tirar do “Outros”, defina a categoria do tipo em Admin → Catálogo (tipos e itens).
                “Cards sem tipo” são tarefas criadas sem escolher o tipo — esses precisam ser corrigidos no próprio card.
              </span>
            </div>
          )}

          {data!.semPrevisao.length > 0 && (
            <div className="mb-4 -mt-1 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <span className="font-medium text-slate-600">Fora da previsão de peças:</span>{" "}
              {data!.semPrevisao.map((s, i) => (
                <span key={`${s.projeto}-${i}`}>
                  {i > 0 && " · "}
                  {s.projeto} <span className="text-slate-400">({s.item}: {s.quantidade})</span>
                </span>
              ))}
              <span className="block mt-0.5 text-slate-400">
                A quantidade desses itens não é quantidade de peças, então eles não geram previsão.
                Para prever esses clientes, cadastre no contrato as peças esperadas por mês.
              </span>
            </div>
          )}

          <div className="space-y-3">
            {cats.map((c) => (
              <div key={c.categoria} className="flex items-center gap-3">
                {/* "Outros" não é uma categoria de verdade: é o resto, de quem
                    não tem categoria no catálogo. Sem dizer o que tem lá dentro,
                    vira caixa-preta ("o que seria esse outros?", Thainá). */}
                <div
                  className="w-32 text-sm text-slate-700 truncate"
                  title={
                    c.categoria === "Outros" && data!.tiposEmOutros.length > 0
                      ? `Tipos sem categoria definida: ${data!.tiposEmOutros.join(", ")}`
                      : c.categoria
                  }
                >
                  {c.categoria}
                  {c.categoria === "Outros" && data!.tiposEmOutros.length > 0 && (
                    <span className="text-slate-400 ml-1 cursor-help">ⓘ</span>
                  )}
                </div>
                {/* Barra em 3 faixas: entregue (verde) · em andamento (azul) ·
                    o resto vazio = o que falta começar. A largura total é o
                    contratado, então dá pra bater o olho e ver o quanto do mês
                    já saiu do papel. */}
                <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden min-w-[60px] flex">
                  <div className="bg-emerald-500 h-full" style={{ width: `${(Math.min(c.entregue, c.contratado || c.entregue) / maxCat) * 100}%` }} />
                  <div className="bg-sky-400 h-full" style={{ width: `${(Math.min(c.emAndamento, Math.max(0, (c.contratado || c.emAndamento) - c.entregue)) / maxCat) * 100}%` }} />
                  <div className="bg-emerald-100 h-full" style={{ width: `${(c.aFazer / maxCat) * 100}%` }} />
                </div>
                <div className="w-24 text-right text-sm tabular-nums text-slate-700">
                  <span className="font-semibold">{c.contratado}</span>
                  <span className="text-xs text-slate-400"> /mês</span>
                </div>
                <div className="w-56 text-right text-xs tabular-nums hidden sm:block whitespace-nowrap">
                  <span className="text-emerald-600">{c.entregue} entregues</span>
                  <span className="text-slate-300"> · </span>
                  <span className="text-sky-600">{c.emAndamento} andando</span>
                  <span className="text-slate-300"> · </span>
                  <span className={c.aFazer > 0 ? "text-slate-500" : "text-slate-300"}>{c.aFazer} do zero</span>
                </div>
                <div className="w-20 text-right text-xs text-slate-400 hidden md:block">
                  {c.clientes} {c.clientes === 1 ? "cliente" : "clientes"}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ContratadoEntregue() {
  const { can } = useAuth();
  const branding = useBranding();
  // MELHORIA 21 — logo do relatório: usa a enviada na Personalização (preta), senão o vetor recdc.
  const logoRel = branding?.logoRelatorioUrl ? anexoUrlAbsoluto(branding.logoRelatorioUrl) : null;
  const [clienteId, setClienteId] = useState("");
  // MELHORIA 21 — período do relatório de entregas por tipo (default: ano corrente).
  const [inicio, setInicio] = useState(`${anoAtual}-01-01`);
  const [fim, setFim] = useState(`${anoAtual}-12-31`);

  const { data: clientes = [] } = useQuery({ queryKey: ["clientes"], queryFn: listarClientes, enabled: can(["gestor", "admin"]) });
  const { data, isLoading, isError } = useQuery({
    queryKey: ["contratado-entregue", clienteId],
    queryFn: () => getContratadoEntregue(clienteId),
    enabled: can(["gestor", "admin"]) && !!clienteId,
  });
  // MELHORIA 21 — entregas por categoria de peça (fim exclusivo: +1 dia).
  const { data: entregas, isLoading: loadEntregas } = useQuery({
    queryKey: ["relatorio-entregas", clienteId, inicio, fim],
    queryFn: () => getRelatorioEntregas(clienteId, inicio, `${fim}T23:59:59`),
    enabled: can(["gestor", "admin"]) && !!clienteId,
  });

  if (!can(["gestor", "admin"])) return <div className="text-slate-500">Acesso restrito.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileBarChart size={22} className="text-blue-600" /> Contratado × Entregue
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Comparativo por cliente — pronto para enviar ao cliente.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500">
            <option value="">Selecione um cliente…</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          {clienteId && (
            <>
              <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className="px-2 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500" title="Entregas a partir de" />
              <span className="text-slate-400 text-xs">até</span>
              <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className="px-2 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500" title="Entregas até" />
            </>
          )}
          {(data || entregas) && (
            <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium">
              <Printer size={15} /> Imprimir / PDF
            </button>
          )}
        </div>
      </div>

      {/* Visão da agência inteira — não depende de escolher cliente. */}
      <ContratadoNoMes />

      {!clienteId && <p className="text-slate-400 text-sm">Escolha um cliente para gerar o relatório detalhado dele.</p>}
      {clienteId && isLoading && <p className="text-slate-400 text-sm">Carregando…</p>}
      {isError && <p className="text-red-500 text-sm">Erro ao gerar o relatório.</p>}

      {/* MELHORIA 21 — cabeçalho apresentável do relatório (imprime bem no PDF).
          Fundo BRANCO de propósito: no print os fundos coloridos não saem, então
          faixa escura + logo branca sumiam no papel. Aqui é tudo foreground
          (texto, bordas, SVG) — a logo recdc PRETA aparece no papel. */}
      {clienteId && (data || entregas) && (
        <div className="rounded-2xl bg-white border border-slate-200 border-l-4 border-l-red-600 border-b-2 border-b-red-600 px-6 py-5 flex items-center justify-between gap-4 print-avoid-break print:rounded-none">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-red-600 font-semibold">Relatório de entregas</p>
            <h2 className="text-2xl font-bold leading-tight text-slate-900">{(data || entregas)!.cliente.nome}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {new Date(inicio).toLocaleDateString("pt-BR")} — {new Date(fim).toLocaleDateString("pt-BR")} · gerado em {new Date().toLocaleDateString("pt-BR")}
            </p>
          </div>
          {logoRel ? (
            <img src={logoRel} alt="logo" className="h-16 max-w-[280px] object-contain shrink-0" />
          ) : (
            <RecLogo className="shrink-0" />
          )}
        </div>
      )}

      {data && (
        <div className="space-y-6">
          {/* Totais */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card icon={<Package size={16} />} label="Itens contratados" valor={data.totais.itensContratados} />
            <Card icon={<CheckCircle2 size={16} />} label="Entregas concluídas" valor={data.totais.entregues} sub={`de ${data.totais.tarefasTotal} tarefas`} cor="text-emerald-600" />
            <Card icon={<Clock size={16} />} label="Horas vendidas" valor={data.totais.horasVendidas ? `${data.totais.horasVendidas}h` : "—"} />
            <Card icon={<Clock size={16} />} label="Horas realizadas" valor={fmtHoras(data.totais.horasRealizadasMs)} cor="text-blue-600" />
          </div>

          {/* Vendido × realizado por nível profissional */}
          {(["junior", "pleno", "senior"] as const).some((n) => data.totais.horasVendidasPorNivel[n] > 0 || data.totais.realizadasMsPorNivel[n] > 0) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-3 text-sm">Horas por nível — vendido × realizado</h3>
              <div className="grid grid-cols-3 gap-3">
                {([["junior", "Júnior"], ["pleno", "Pleno"], ["senior", "Sênior"]] as const).map(([n, lbl]) => {
                  const vend = data.totais.horasVendidasPorNivel[n];
                  const real = data.totais.realizadasMsPorNivel[n];
                  const estourou = vend > 0 && real > vend * 3600000;
                  return (
                    <div key={n} className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs font-semibold text-slate-500">{lbl}</p>
                      <p className="text-sm text-slate-700 mt-1">Vendido: <strong>{vend ? `${vend}h` : "—"}</strong></p>
                      <p className={`text-sm mt-0.5 ${estourou ? "text-red-600" : "text-slate-700"}`}>Realizado: <strong>{fmtHoras(real)}</strong></p>
                    </div>
                  );
                })}
              </div>
              {data.totais.realizadasMsSemNivel > 0 && (
                <p className="text-xs text-slate-500 mt-2">
                  Sem nível configurado: <strong>{fmtHoras(data.totais.realizadasMsSemNivel)}</strong> realizadas (não entram na quebra acima).
                </p>
              )}
              <p className="text-[11px] text-slate-400 mt-1">O realizado por nível usa o nível profissional de quem apontou as horas (configurável em Usuários).</p>
            </div>
          )}

          {/* Por projeto */}
          {data.projetos.length === 0 ? (
            <p className="text-sm text-slate-400">Nenhum projeto ativo para este cliente.</p>
          ) : (
            <div className="space-y-4">
              {data.projetos.map((p) => (
                <div key={p.projetoId} className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h3 className="font-semibold text-slate-800">{p.nome}</h3>
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600">{STATUS_LABEL[p.status] || p.status}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 text-sm">
                    <Mini label="Itens contratados" valor={String(p.itensContratadosQtd)} />
                    <Mini label="Entregas" valor={`${p.entregues}/${p.tarefasTotal}`} />
                    <Mini label="Horas vendidas" valor={p.horasVendidas ? `${p.horasVendidas}h` : "—"} />
                    <Mini label="Horas realizadas" valor={fmtHoras(p.horasRealizadasMs)} />
                  </div>
                  {p.itensContratados.length > 0 && (
                    <div className="border-t border-slate-100 pt-2">
                      <p className="text-xs font-semibold uppercase text-slate-400 mb-1">Itens contratados</p>
                      <div className="divide-y divide-slate-50">
                        {p.itensContratados.map((it, i) => (
                          <div key={i} className="flex items-center gap-2 py-1 text-sm">
                            <span className="font-mono text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{it.quantidade}×</span>
                            <span className="flex-1 text-slate-700">{it.label}{[fmtFormato(it.formato), it.especificacoes].filter(Boolean).length > 0 && <span className="text-slate-400"> · {[fmtFormato(it.formato), it.especificacoes].filter(Boolean).join(" · ")}</span>}</span>
                            {it.horasVendidas != null && <span className="text-xs text-slate-400">{it.horasVendidas}h</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MELHORIA 21 — Entregas por TIPO DE PEÇA (contratado × entregue + peças). */}
      {clienteId && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Layers size={18} className="text-blue-600" />
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Entregas por tipo de peça</h2>
            {loadEntregas && <Loader2 size={15} className="animate-spin text-slate-400" />}
          </div>

          {entregas && entregas.categorias.length === 0 && entregas.pecasEntregues.length === 0 && (
            <p className="text-sm text-slate-400">Nenhuma entrega no período (ajuste as datas acima). Marque a <strong>categoria de relatório</strong> em cada tipo no Catálogo pra agrupar aqui.</p>
          )}

          {entregas && (entregas.categorias.length > 0 || entregas.pecasEntregues.length > 0) && (
            <>
              {/* Cards por categoria: entregue / contratado (+adicionais / pendente) */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {entregas.categorias.map((c) => {
                  const cor = c.adicionais > 0 ? "text-blue-600" : c.pendente > 0 ? "text-amber-600" : "text-emerald-600";
                  return (
                    <div key={c.categoria} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 print-avoid-break">
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{c.categoria}</p>
                      <p className="mt-1"><span className={`text-3xl font-bold ${cor}`}>{c.entregue}</span><span className="text-lg text-slate-400">/{c.contratado}</span></p>
                      <p className="text-[11px] mt-0.5">
                        {c.adicionais > 0 ? <span className="text-blue-600 font-medium">+{c.adicionais} adicionais</span>
                          : c.pendente > 0 ? <span className="text-amber-600 font-medium">{c.pendente} pendente(s)</span>
                          : <span className="text-emerald-600 font-medium">meta cumprida</span>}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* MELHORIA 22 — Horas: vendido × realizado por tipo (dentro da categoria). */}
              {entregas.horasCategorias.length > 0 && (
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5 print-avoid-break">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-3 text-sm flex items-center gap-1.5">
                    <Clock size={15} className="text-blue-500" /> Horas — vendido × realizado por tipo
                  </h3>
                  <div className="space-y-3">
                    {entregas.horasCategorias.map((c) => {
                      const estourou = c.vendidoH > 0 && c.realizadoMs > c.vendidoH * 3600000;
                      return (
                        <div key={c.categoria} className="border-b border-slate-100 dark:border-slate-800 pb-2 last:border-0 last:pb-0">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-semibold text-slate-700 dark:text-slate-200">{c.categoria}</span>
                            <span className="tabular-nums">
                              <span className="text-slate-500">vendido </span><strong className="text-slate-700 dark:text-slate-200">{fmtH(c.vendidoH)}</strong>
                              <span className="text-slate-300 dark:text-slate-600"> · </span>
                              <span className="text-slate-500">realizado </span><strong className={estourou ? "text-red-600" : "text-blue-600"}>{fmtHoras(c.realizadoMs)}</strong>
                              {estourou && <span className="text-red-600"> ⚠️</span>}
                            </span>
                          </div>
                          <div className="mt-1 pl-3 space-y-0.5">
                            {c.tipos.map((t) => {
                              const te = t.vendidoH > 0 && t.realizadoMs > t.vendidoH * 3600000;
                              return (
                                <div key={t.tipoTarefaId} className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                                  <span className="truncate">{t.tipoIcone} {t.tipoLabel}</span>
                                  <span className="tabular-nums shrink-0">{fmtH(t.vendidoH)} <span className="text-slate-300 dark:text-slate-600">/</span> <span className={te ? "text-red-500 font-medium" : ""}>{fmtHoras(t.realizadoMs)}</span></span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">Vendido = horas do contrato por tipo · Realizado = horas úteis trabalhadas nas tarefas daquele tipo (todo o projeto). ⚠️ = passou do vendido.</p>
                </div>
              )}

              {/* Peças entregues (clicáveis → abre a tarefa) */}
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2 text-sm flex items-center gap-1.5">
                  <CheckCircle2 size={15} className="text-emerald-500" /> Peças entregues
                  <span className="text-slate-400 font-normal">· {entregas.pecasEntregues.reduce((s, p) => s + p.quantidade, 0)} peças em {entregas.pecasEntregues.length} tarefas</span>
                </h3>
                {entregas.pecasEntregues.length === 0 ? <p className="text-sm text-slate-400">Nada entregue no período.</p> : (
                  <div className="divide-y divide-slate-50 dark:divide-slate-800">
                    {entregas.pecasEntregues.map((p) => (
                      <Link key={p.id} to={`/tarefas?tarefa=${p.id}`} className="flex items-center gap-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded px-1 -mx-1">
                        <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded px-1.5 py-0.5 shrink-0">{p.quantidade}×</span>
                        <span className="flex-1 text-slate-700 dark:text-slate-200 truncate">{p.tipoIcone} {p.titulo}</span>
                        <span className="text-[11px] text-slate-400 shrink-0 hidden sm:inline">{p.categoria}</span>
                        <span className="text-[11px] text-slate-400 shrink-0 tabular-nums">{new Date(p.data).toLocaleDateString("pt-BR")}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Em andamento / pendente */}
              {entregas.emAndamento.length > 0 && (
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2 text-sm flex items-center gap-1.5">
                    <Clock size={15} className="text-amber-500" /> Em andamento / pendente
                    <span className="text-slate-400 font-normal">· {entregas.emAndamento.length}</span>
                  </h3>
                  <div className="divide-y divide-slate-50 dark:divide-slate-800">
                    {entregas.emAndamento.map((p) => (
                      <Link key={p.id} to={`/tarefas?tarefa=${p.id}`} className="flex items-center gap-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded px-1 -mx-1">
                        <span className="flex-1 text-slate-700 dark:text-slate-200 truncate">{p.tipoIcone} {p.titulo}</span>
                        <span className="text-[11px] text-slate-400 shrink-0 hidden sm:inline">{p.categoria}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 shrink-0">{p.status}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Card({ icon, label, valor, sub, cor = "text-slate-800" }: { icon: React.ReactNode; label: string; valor: string | number; sub?: string; cor?: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 print-avoid-break">
      <p className="text-slate-500 text-xs flex items-center gap-1.5">{icon} {label}</p>
      <p className={`text-2xl font-bold mt-1 ${cor}`}>{valor}</p>
      {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
function Mini({ label, valor }: { label: string; valor: string }) {
  return (
    <div>
      <p className="text-[11px] text-slate-400">{label}</p>
      <p className="font-semibold text-slate-700 tabular-nums">{valor}</p>
    </div>
  );
}
